import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, TextField, InputAdornment, Alert,
  Divider, MenuItem, Select, FormControl, InputLabel,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import axios from '../../api/axios';

// Project-level settings panel — markup %, tax %, co-approver +
// threshold. The co-approver is picked from existing homeowners in the
// system; invite-new-user is handled elsewhere.
const ProjectSettingsDialog = ({ open, onClose, project, onSaved }) => {
  const [markup, setMarkup] = useState('');
  const [tax, setTax] = useState('');
  const [coApproverId, setCoApproverId] = useState('');
  const [threshold, setThreshold] = useState('');
  const [homeowners, setHomeowners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && project) {
      setMarkup(project.markup_rate ?? '');
      setTax(project.tax_rate ?? '');
      setCoApproverId(project.co_approver_id ?? '');
      setThreshold(project.co_approver_threshold ?? '');
      setError('');
      axios.get('/api/projects/homeowners')
        .then((r) => setHomeowners(r.data.homeowners || []))
        .catch(() => setHomeowners([]));
    }
  }, [open, project]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`/api/projects/${project.id}`, {
        markup_rate: markup === '' ? 0 : Number(markup),
        tax_rate: tax === '' ? 0 : Number(tax),
        co_approver_id: coApproverId === '' ? null : coApproverId,
        co_approver_threshold: threshold === '' ? null : Number(threshold),
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SettingsIcon /> Project settings
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Pricing</Typography>
        <Box display="flex" gap={2} mb={3}>
          <TextField
            label="Markup"
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            value={markup}
            onChange={(e) => setMarkup(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText="Applied to subtotal before tax"
            fullWidth
          />
          <TextField
            label="Tax rate"
            type="number"
            inputProps={{ min: 0, step: '0.01' }}
            value={tax}
            onChange={(e) => setTax(e.target.value)}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
            helperText="Applied after markup"
            fullWidth
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Co-approver (optional)</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          A second person who needs to sign off on items above the threshold. Useful for
          joint decisions — e.g. spouse on big-ticket renovations.
        </Typography>
        <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
          <InputLabel>Co-approver</InputLabel>
          <Select
            value={coApproverId}
            onChange={(e) => setCoApproverId(e.target.value)}
            label="Co-approver"
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {homeowners.map((h) => (
              <MenuItem key={h.id} value={h.id}>
                {h.first_name} {h.last_name} ({h.email})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Threshold"
          type="number"
          inputProps={{ min: 0, step: '0.01' }}
          value={threshold}
          onChange={(e) => setThreshold(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          helperText="Items costing more than this require co-approval"
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProjectSettingsDialog;
